import lastDayOfYear from 'date-fns/lastDayOfYear';
import lastDayOfQuarter from 'date-fns/lastDayOfQuarter';
import lastDayOfMonth from 'date-fns/lastDayOfMonth';
import { IDataChart, IItemChart, IPrepareDataChart, IPrepareItemChart } from '@/creditflowresearch/domain/vo/Bond/IDataChart';
import { Periods } from '@/creditflowresearch/ui/common/constants';
import { ALL_OTHERS_COLOR, ALL_OTHERS_NAME, ChartsGrouping } from '@/creditflowresearch/application/Bonds/charts/constants';
import colors from './chartColors';

const getLastTimestampByPeriod = (timestamp: number, period: Periods): number => {
  switch (period) {
    case Periods.YEAR:
      return lastDayOfYear(timestamp).valueOf();
    case Periods.QUARTER:
      return lastDayOfQuarter(timestamp).valueOf();
    case Periods.MONTH:
      return lastDayOfMonth(timestamp).valueOf();
    default:
      return timestamp;
  }
};

interface ICollectionPreparedItem {
  total: number,
  [timestamp: number]: IPrepareItemChart,
}

const aggregateChartDataByX = (
  acc: ICollectionPreparedItem,
  item:IItemChart,
  x: number,
  y: number,
): ICollectionPreparedItem => {
  const result = { ...acc };

  if (!(x in result)) {
    result[x] = {
      x,
      y: 0,
      date: item.date,
      dealCount: 0,
      dealSize: 0,
    };
  }

  result[x].y += y;
  result[x].dealCount += item.dealCount;
  result[x].dealSize += item.dealSize;
  result.total += y;

  return result;
};

const groupDataByPeriod = (
  data: IItemChart[],
  period: Periods,
  grouping: ChartsGrouping,
): { data: IPrepareItemChart[], total: number } => {
  const groupData = data.reduce((acc, item) => {
    const currentTimestamp = (new Date(item.date)).valueOf();
    const periodTimestamp = getLastTimestampByPeriod(currentTimestamp, period);
    const y = grouping === ChartsGrouping.VOLUME ? item.dealSize : item.dealCount;

    return aggregateChartDataByX(acc, item, periodTimestamp, y);
  }, { total: 0 });

  const { total, ...prepareData } = groupData;

  return { data: Object.values(prepareData), total };
};

export const prepareAndGroupData = (
  data:IDataChart[],
  period: Periods,
  grouping: ChartsGrouping,
):IPrepareDataChart[] => data.map((item) => ({
  name: item.name,
  color: item.color,
  ...groupDataByPeriod(item.dealChartData, period, grouping),
}));

export const initColors = (data:IDataChart[]) => {
  const totalColors = colors.length;

  data.forEach((item, index) => {
    const colorIndex = index % totalColors;
    item.color = colors[colorIndex];
  });
};

const getAggregateItemsOfChart = (data: IPrepareDataChart[]): { data: IPrepareItemChart[], total: number } => {
  const unionData = data.reduce((acc, item) => {
    item.data.forEach((itemData) => {
      acc = aggregateChartDataByX(acc, itemData, itemData.x, itemData.y);

      if (!('detailData' in acc[itemData.x])) {
        acc[itemData.x].detailData = [];
      }

      acc[itemData.x].detailData.push({
        name: item.name,
        ...itemData,
      });
    });

    return acc;
  }, { total: 0 });

  const { total, ...prepareData } = unionData;

  return { data: Object.values(prepareData), total };
};

export const getGroupList = (data: IPrepareDataChart[], count: number = 10): IPrepareDataChart[] => {
  if (data.length <= count) {
    return data;
  }

  const sortData = data.sort((item1, item2) => item2.total - item1.total);
  const otherData = sortData.splice(count);

  const allOthers: IPrepareDataChart = {
    name: ALL_OTHERS_NAME,
    color: ALL_OTHERS_COLOR,
    ...getAggregateItemsOfChart(otherData),
  };

  allOthers.data.sort((item1, item2) => item1.x - item2.x);

  return [...sortData, allOthers];
};

const getXRange = (series: { xData: number[] }[]) => {
  const range = new Set<number>();

  series.forEach((item) => {
    item.xData.forEach((x) => range.add(x));
  });

  return Array.from(range).sort();
};

// chart - is object of Highcharts
export const setExtremesOfChart = (chart, maxCountPeriods) => {
  if (chart.series.length && chart.xAxis.length) {
    const xAxis = chart.xAxis[0];
    const xRange = getXRange(chart.series);
    const countPeriods = xRange.length;
    const startIndex = maxCountPeriods < countPeriods ? (countPeriods - maxCountPeriods - 1) : 0;
    const endIndex = countPeriods - 1;

    xAxis.setExtremes(xRange[startIndex], xRange[endIndex]);
  }
};
